import { useCallback, useMemo, useRef } from "react";
import {
	BaseListContainer,
	BaseListFooterContainer,
	BaseListHeaderContainer,
	BaseListInfoArea,
	CommonListProps,
} from "./OptionListsComponents";
import i18nPackage from "../../../i18n/i18n";
import { CoreList, CoreListRow, CoreNoResults, CorePagination } from "../../../core";
import { FlaggedKeyword } from "@verity/database/prisma";
import { useFocusElementOnMount } from "../../../hooks";
import useEditableList from "../hooks/useEditableList";
import { EditableListCell, EditableListMenuButton, EditableListToolbar, EditableSelectAllRow } from "./EditableList";
import FlaggedListIcon from "../../images/configuration/keyword-list.svg?react";
import { SvgIcon, SvgIconProps, Typography } from "@mui/material";

const EmptyBoxIcon = (props: SvgIconProps) => {
	return (
		<SvgIcon {...props}>
			<svg width="140" height="97" viewBox="0 0 140 97" fill="none" xmlns="http://www.w3.org/2000/svg">
				<path
					d="M124.867 75.1432L59.3201 95.3114L16.1024 75.1432L15.6908 53.5344L6.7386 49.2127L16.1024 35.5271L1.69653 23.2821L71.5651 1.67334L81.6493 13.9183L94.6146 1.67334L137.832 23.2821L124.867 35.5271L137.832 49.2127L135.671 49.9329L124.867 53.8177V75.1432Z"
					fill="#ECEAF1"
				/>
				<path
					d="M59.3201 57.1359V95.3114M59.3201 57.1359L48.5157 69.3808L15.6908 53.5344M59.3201 57.1359L124.867 35.5271M59.3201 57.1359L71.5651 72.9823L135.671 49.9329M59.3201 57.1359L16.1024 35.5271M59.3201 95.3114L124.867 75.1432V53.5344L135.671 49.9329M59.3201 95.3114L16.1024 75.1432L15.6908 53.5344M124.867 35.5271L137.832 49.2127L135.671 49.9329M124.867 35.5271L137.832 23.2821L94.6146 1.67334L81.6493 13.9183M124.867 35.5271L81.6493 13.9183M81.6493 13.9183L71.5651 1.67334L1.69653 23.2821L16.1024 35.5271M81.6493 13.9183V49.2127M81.6493 13.9183L16.1024 35.5271M16.1024 35.5271L6.7386 49.2127L15.6908 53.5344"
					stroke="#35363A"
					strokeWidth="1.44059"
				/>
			</svg>
		</SvgIcon>
	);
};

export type FlaggedKeywordWithInstituteLevel = FlaggedKeyword & { isInstituteLevel?: boolean };

const PAGE_SIZE = 4;

const convertKeywordToComparableString = (keyword: string) =>
	keyword.trim().toLowerCase() as string & { __comparable: true };

type FlaggedKeywordsListProps = {
	viewingContext: "quiz" | "institute";
	keywords: FlaggedKeywordWithInstituteLevel[];
	onSettingChange: (keywords: FlaggedKeywordWithInstituteLevel[]) => void;
} & CommonListProps;

export default function FlaggedKeywordsList(props: FlaggedKeywordsListProps) {
	const { viewingContext, keywords, onSettingChange } = props;

	const { ref } = useFocusElementOnMount<HTMLDivElement>(props.focusOnMount, props.onFocus);
	const toolbarRef = useRef<HTMLInputElement | null>(null);

	const { pagination, searchState, tableActions, rows, tableState } = useEditableList<FlaggedKeywordWithInstituteLevel>(
		{
			initialItems: keywords,
			onUpdate: onSettingChange,
			getDisabledState: (row) => Boolean(row.isInstituteLevel && viewingContext === "quiz"),
			pageSize: PAGE_SIZE,
			makeItem: (val) => ({
				keyword: val,
				isInstituteLevel: viewingContext !== "quiz",
			}),
		},
	);

	const { baseTableRows, tableRows } = rows;
	const { page, paginatedRows, setPage, totalPages } = pagination;
	const { searchQuery, setSearchQuery } = searchState;
	const { allSelected, areAllDisabled, selectedCount, hasSelection } = tableState;
	const {
		toggleSelectOne,
		toggleEditOne,
		handleUpdate: updateDraftKeyword,
		handleAdd,
		handleDeleteOne,
		handleDeleteSelected,
	} = tableActions;

	// handlers
	const canAddOrEdit = useCallback(
		(keyword: string) => {
			const existingKeywords = baseTableRows.map((x) => convertKeywordToComparableString(x.keyword));

			const isKeywordAlreadySet = existingKeywords.includes(convertKeywordToComparableString(keyword));
			const isKeywordEmpty = keyword.trim().length === 0;
			const hasSpecialCharacters = !/^[a-zA-Z'-\s]+$/.test(keyword.trim());
			const isKeywordInvalid = isKeywordAlreadySet || isKeywordEmpty || hasSpecialCharacters;

			return !isKeywordInvalid;
		},
		[baseTableRows],
	);

	const handleSaveKeyword = useCallback(
		(id: string, updates: Partial<FlaggedKeywordWithInstituteLevel>) => {
			updateDraftKeyword(id, updates);
			toolbarRef.current?.focus();
		},
		[updateDraftKeyword],
	);

	const badgeText = useMemo(() => {
		if (viewingContext === "quiz" && keywords.length > 0) {
			return i18nPackage.t("editQuizPage.flaggedKeywordsList.badgeText.quiz", { count: keywords.length });
		}

		if (viewingContext === "institute" && selectedCount > 0) {
			return i18nPackage.t("editQuizPage.flaggedKeywordsList.badgeText.setting", { count: selectedCount });
		}

		return undefined;
	}, [keywords.length, selectedCount, viewingContext]);

	return (
		<BaseListContainer ref={ref}>
			<BaseListHeaderContainer>
				<BaseListInfoArea
					title={
						viewingContext === "quiz"
							? i18nPackage.t("editQuizPage.flaggedKeywordsList.title")
							: i18nPackage.t("editQuizPage.flaggedKeywordsList.settingTitle")
					}
					description={i18nPackage.t("editQuizPage.flaggedKeywordsList.description")}
					slotProps={{
						...(badgeText && {
							badge: {
								text: badgeText,
							},
						}),
					}}
					icon={<FlaggedListIcon />}
				/>
				<EditableListToolbar
					searchQuery={searchQuery}
					onSearchChange={setSearchQuery}
					handleAdd={handleAdd}
					canAddOrEdit={canAddOrEdit}
					placeholder={i18nPackage.t("editQuizPage.flaggedKeywordsList.placeholder")}
					slotProps={{
						textfield: {
							inputRef: toolbarRef,
						},
					}}
				/>
			</BaseListHeaderContainer>
			{tableRows.length !== 0 && viewingContext === "institute" && (
				<EditableSelectAllRow
					checked={allSelected}
					disabled={areAllDisabled}
					onChange={(checked) => {
						paginatedRows.forEach((x) => {
							if (x.disabled) return;
							toggleSelectOne(x.id, checked);
						});
					}}
					button={{
						disabled: !hasSelection,
						onRemove: handleDeleteSelected,
					}}
				/>
			)}
			<BaseListFooterContainer>
				{tableRows.length === 0 && viewingContext === "institute" ? (
					<CoreNoResults
						header={i18nPackage.t("editQuizPage.flaggedKeywordsList.noResults.title")}
						image={<EmptyBoxIcon sx={{ width: "175px", height: "97px" }} />}
					>
						<Typography>{i18nPackage.t("editQuizPage.flaggedKeywordsList.noResults.subtitle")}</Typography>
					</CoreNoResults>
				) : (
					<CoreList maxHeight={350}>
						{tableRows.map((row) => (
							<CoreListRow
								key={row.id}
								checked={viewingContext === "institute" ? row.selected : undefined}
								onSelect={viewingContext === "institute" ? (checked) => toggleSelectOne(row.id, checked) : undefined}
								disabled={row.disabled}
								label={
									<EditableListCell<FlaggedKeywordWithInstituteLevel>
										row={row}
										onUpdate={handleSaveKeyword}
										onRemove={() => toggleEditOne(row.id)}
										canAddOrEdit={canAddOrEdit}
										dataKey="keyword"
										slotProps={{
											badge: {
												disabledLabel: i18nPackage.t("editQuizPage.flaggedKeywordsList.disabledBadge"),
												disabledTooltip: i18nPackage.t("editQuizPage.flaggedKeywordsList.disabledRow"),
											},
										}}
										maxWidth={viewingContext === "institute" ? 650 : 500}
									/>
								}
								action={
									!row.editing &&
									!row.disabled && (
										<EditableListMenuButton
											onEdit={() => toggleEditOne(row.id)}
											onDelete={() => handleDeleteOne(row.id)}
										/>
									)
								}
							/>
						))}
					</CoreList>
				)}
				{baseTableRows.length > PAGE_SIZE && (
					<CorePagination page={page} count={totalPages} onChange={(_, p) => setPage(p)} />
				)}
			</BaseListFooterContainer>
		</BaseListContainer>
	);
}
